import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectSelectedProjectId } from 'store/projects/selectors/projectsSelectors';
import { getProjectMediaTagsData } from '../services/getProjectMediaTagsData.service';

/**
 * Reads the selected project's tags again — the same read the gallery makes
 * when it opens (`loadProjectMediaTagsData`), repeated later.
 *
 * The gallery reads tags only once, and nothing pushes later changes to it. The
 * AI tagger writes its tags 10–15 s after a photo is filed, by when the gallery
 * has usually opened, so a fresh photo showed no tags until a reload. Opening an
 * image asks again, and so does an upload, a little later
 * (`scheduleProjectMediaTagsRefresh`).
 *
 * Throttled per project, because paging through a gallery would otherwise read
 * every tag in the project on every step. `force` skips the throttle, for the
 * read after an upload, which must not be swallowed by an open just before it.
 */

const MIN_INTERVAL_MS = 15 * 1000;

const lastRefreshAtByProjectId = {};

export const refreshProjectMediaTagsData = createAsyncThunk(
  'mediaTags/refreshProjectMediaTagsData',
  async (_, { getState }) =>
    getProjectMediaTagsData(selectSelectedProjectId(getState())),
  {
    condition: ({ force = false } = {}, { getState }) => {
      const projectId = selectSelectedProjectId(getState());
      if (!projectId) return false;

      const now = Date.now();
      const lastRefreshAt = lastRefreshAtByProjectId[projectId] || 0;
      if (!force && now - lastRefreshAt < MIN_INTERVAL_MS) return false;

      lastRefreshAtByProjectId[projectId] = now;
      return true;
    },
  }
);
